import { logger } from "@/services/LoggingService";
import { useMultiWealthica2Store } from "@/stores/multiWealthica2Store";
import { organizePositionsByAccount } from "@/providers/wealthica2/utils/account-position-utils";
import { getCachedPositionsArray } from "@/providers/wealthica2/utils/cache-utils";
import { supabase } from "@/integrations/supabase/client";

export interface BuildPortfolioHandlersParams {
  investmentHoldings: any[];
  refreshHoldings: () => void;
  setCollectedPositions: (positions: any[]) => void;
  setShowAssetTypeFilterDialog: (open: boolean) => void;
}

export const createBuildPortfolioHandlers = ({
  investmentHoldings,
  refreshHoldings,
  setCollectedPositions,
  setShowAssetTypeFilterDialog
}: BuildPortfolioHandlersParams) => {
  
  // Fallback: collect view-only positions from Wealthica multi-store (no DB persistence)
  const collectViewOnlyPositions = (): any[] => {
    try {
      const store = useMultiWealthica2Store.getState();
      // Aggregate raw positions across all connections
      const rawPositionsAll: any[] = [];
      const accountsInfo: Record<string, string> = {};
      for (const conn of store.connections.values()) {
        // Accounts map for display names
        (conn.accounts || []).forEach((acc: any) => {
          accountsInfo[acc.id] = acc.name || acc.id;
        });
        if (Array.isArray(conn.positions) && conn.positions.length > 0) {
          rawPositionsAll.push(...conn.positions);
        } else if (conn.institutionId) {
          // Fallback: pull from session/local cache if available
          const cached = getCachedPositionsArray(conn.institutionId);
          if (cached.length > 0) rawPositionsAll.push(...cached);
        }
      }

      if (rawPositionsAll.length === 0) return [];

      // Normalize to per-account positions
      const { accountMap } = organizePositionsByAccount(rawPositionsAll as any[], accountsInfo);
      const normalized: any[] = [];
      Object.entries(accountMap).forEach(([accountId, positions]) => {
        (positions as any[]).forEach((pos: any) => {
          const inv = Array.isArray(pos.investments) ? pos.investments[0] : undefined;
          const derivedAccountId = inv?.investment || pos.account_id || accountId;
          if (!derivedAccountId) return;
          normalized.push({
            id: pos._id || pos.id || `${derivedAccountId}-${pos.security?.symbol || pos.security?.name || Math.random()}`,
            accountId: derivedAccountId,
            name: pos.security?.name || pos.name || 'Security',
            symbol: pos.security?.symbol || pos.symbol || '',
            quantity: typeof pos.quantity === 'number' ? pos.quantity : (inv?.quantity || 0),
            book_value: typeof pos.book_value === 'number' ? pos.book_value : (inv?.book_value || 0),
            market_value: typeof pos.market_value === 'number' ? pos.market_value : (inv?.market_value || 0),
            currency: pos.currency || inv?.currency || 'USD',
            securityType: pos.security?.type || pos.class || 'unknown',
            provider: 'wealthica2'
          });
        });
      });
      return normalized;
    } catch (error) {
      logger.warn('Build Portfolio', 'Failed to collect view-only positions', { error });
      return [];
    }
  };
  
  /**
   * Handle Build Portfolio (All Holdings) - Async event handler
   */
  const handleBuildPortfolio = async () => {
    // Refresh holdings data first to ensure real-time accuracy
    logger.info('Build Portfolio', 'Refreshing investment holdings for real-time asset counts');
    
    // Await the refresh to ensure data is fresh before proceeding
    const refetchResult: any = await refreshHoldings();
    
    // Prefer fresh data from refetch result; fallback to captured state
    const freshHoldings: any[] = (refetchResult && 'data' in refetchResult && refetchResult.data)
      ? (refetchResult.data as any[])
      : (investmentHoldings || []);
    
    // Primary: Use ALL investment holdings from database as source of truth
    let positions = (freshHoldings || []).map(holding => ({
      id: holding.id,
      accountId: holding.account_id || 'manual',
      name: holding.asset_name,
      symbol: holding.symbol || '',
      quantity: holding.quantity || 0,
      book_value: holding.book_value || 0,
      market_value: holding.market_value || 0,
      currency: holding.currency || 'USD',
      securityType: holding.asset_type,
      asset_type: holding.asset_type,
      provider: holding.is_manual ? 'manual' : (holding.provider_name || 'external')
    })) || [];
    
    // Fallback: if DB has no holdings (persistence OFF), use view-only positions from store
    if ((positions?.length || 0) === 0) {
      const viewOnly = collectViewOnlyPositions();
      if (viewOnly.length > 0) {
        logger.info('Build Portfolio - VIEW-ONLY FALLBACK', 'Using in-memory Wealthica positions for dialog', {
          viewOnlyCount: viewOnly.length
        });
        positions = viewOnly;
      }
    }
    
    const manualCount = ((positions || []).filter(p => p.provider === 'manual')?.length || 0);
    const externalCount = ((positions || []).filter(p => p.provider !== 'manual')?.length || 0);
    
    logger.info('Build Portfolio - REAL-TIME ASSET COUNT', `Passing fresh database holdings to filter dialog`, {
      totalDatabaseHoldings: freshHoldings?.length || 0,
      manualCount,
      externalCount,
      positionsCollected: positions?.length || 0,
      providers: Array.from(new Set((positions || []).map(p => p.provider || 'unknown'))),
      samplePositions: (positions || []).slice(0, 3).map(p => ({ id: p.id, name: p.name, provider: p.provider }))
    });
    
    // === Synthesize Multi-Asset accounts as positions when no look-through holdings exist ===
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        const accountsWithHoldingsUuid = new Set((freshHoldings || [])
          .map((h: any) => h.account_uuid)
          .filter(Boolean));
        const accountsWithHoldingsNum = new Set((freshHoldings || [])
          .map((h: any) => h.account_id)
          .filter(Boolean));

        const query = supabase
          .from('investment_accounts')
          .select('id, account_number, account_name, current_balance')
          .eq('user_id', user.id)
          .eq('cdn_asset_class', 'multi_asset') as any;
        const { data: candidateAccounts, error: acctErr } = await query;

        if (!acctErr && Array.isArray(candidateAccounts) && candidateAccounts.length > 0) {
          const synthetic = candidateAccounts
            .filter(acc => !accountsWithHoldingsUuid.has(acc.id) && !accountsWithHoldingsNum.has(acc.account_number))
            .map(acc => ({
              // Use the account UUID as the synthetic asset_id so it is a valid UUID
              id: acc.id,
              accountId: acc.id,
              name: acc.account_name || 'Multi-Asset Account',
              symbol: '',
              quantity: 1,
              book_value: typeof acc.current_balance === 'number' ? acc.current_balance : 0,
              market_value: typeof acc.current_balance === 'number' ? acc.current_balance : 0,
              currency: 'CAD',
              securityType: 'multi_asset',
              asset_type: 'multi_asset',
              provider: 'external'
            }));
          if (synthetic.length > 0) {
            positions = [...positions, ...synthetic];
            logger.info('Build Portfolio - MULTI-ASSET SYNTHETIC', 'Added synthetic multi-asset account positions', {
              synthesized: synthetic.length,
              sample: synthetic.slice(0, 3)
            });
          }
        } else if (acctErr) {
          logger.warn('Build Portfolio - MULTI-ASSET SYNTHETIC', 'Accounts query failed', { error: acctErr });
        }
      }
    } catch (error) {
      logger.warn('Build Portfolio - MULTI-ASSET SYNTHETIC', 'Failed to synthesize multi-asset accounts', { error });
    }

    // Save positions for dialog and open it
    setCollectedPositions(positions);
    setShowAssetTypeFilterDialog(true);
  };

  /**
   * Handle Build Portfolio (Manual Only) - Async event handler
   */
  const handleBuildPortfolioManualOnly = async () => {
    // Refresh holdings data first to ensure real-time accuracy
    logger.info('Build Portfolio (Manual Only)', 'Refreshing investment holdings for real-time manual asset counts');
    
    // Await the refresh to ensure data is fresh before proceeding
    const refetchResult: any = await refreshHoldings();
    const freshHoldings: any[] = (refetchResult && 'data' in refetchResult && refetchResult.data)
      ? (refetchResult.data as any[])
      : (investmentHoldings || []);
    
    // Use only manual assets for this specific option
    const positions = freshHoldings
      ?.filter(holding => holding.is_manual)
      .map(holding => ({
        id: holding.id,
        accountId: holding.account_id || 'manual',
        name: holding.asset_name,
        symbol: holding.symbol || '',
        quantity: holding.quantity || 0,
        book_value: holding.book_value || 0,
        market_value: holding.market_value || 0,
        currency: holding.currency || 'USD',
        securityType: holding.asset_type,
        asset_type: holding.asset_type,
        provider: 'manual'
      })) || [];
    
    // Save positions for dialog and open it
    setCollectedPositions(positions);
    setShowAssetTypeFilterDialog(true);
  };

  return {
    handleBuildPortfolio,
    handleBuildPortfolioManualOnly
  };
};
